import React, { useState } from "react";
import { ChevronDownIcon, BookmarkIcon, PlusIcon, TrashIcon } from "lucide-react";
import { Button } from "@/components/ui";
import { useAddEditSchoolPoc } from "./useAddEditSchoolPoc";
import { SchoolPoc } from "./consts";
import { Bookmark } from "lucide-react";
import {
  POC_NAME,
  POC_DESIGNATION,
  POC_DEPARTMENT,
  POC_PHONE,
  POC_EMAIL,
} from "./consts";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { LOGIN_ROLES } from "@/lib/utils";

const AddEditSchoolPoc = () => {
  const {
    schoolPocs,
    isLoadingPocs,
    isUpdating,
    localPocs,
    isAddingNew,
    validationErrors,
    handleSaveChanges,
    handleAddNewRow,
    handleDeletePocLocal,
    handlePocFieldChange,
    handleCancelNewRow,
  } = useAddEditSchoolPoc();

  const [isExpanded, setIsExpanded] = useState(true);

  const loggedInUserData = useSelector(
      (state: RootState) => state.currentData.loggedInUserData
    );
  
    // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
    const roles = loggedInUserData?.role || [];
  
    // Helper to check if user has a given role
    const hasRole = (role: string) => Array.isArray(roles) && roles.includes(role);


  if (isLoadingPocs) {
    return (
      <div className="bg-white p-5">
        <h2 className="text-[18px] text-primary mb-5">School POC Details</h2>
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">Loading POC data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-[18px] text-primary">Lab POC Details</h2>
          <div className="flex items-center gap-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer"
          >
            <span>Collapse</span>
            <ChevronDownIcon className="w-4 h-4" />
          </button>
          {(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN")) && (
          <Button
            type="button"
            variant="default"
            aria-label="Save POCs"
            onClick={handleSaveChanges}
            disabled={isUpdating}
          >
            Save
          </Button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-4">
          {/* POC Form Rows */}
          {localPocs.length > 0 ? (
            <div className="space-y-4">
              {localPocs.map((poc: SchoolPoc, index: number) => (
                <div
                  key={poc.id}
                  className="p-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        POC - Name *
                      </label>
                      <input
                        type="text"
                        value={poc.pocName}
                        onChange={(e) =>
                          handlePocFieldChange(
                            poc.id!,
                            POC_NAME,
                            e.target.value
                          )
                        }
                        placeholder="Please enter"
                        className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 ${
                          validationErrors[`${poc.id}-${POC_NAME}`]
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                        disabled={!hasRole("SALES_OPS") && !hasRole("LABS_TEAM_HEAD") && !hasRole("SUPER_ADMIN")}
                      />
                      {validationErrors[`${poc.id}-${POC_NAME}`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors[`${poc.id}-${POC_NAME}`]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        POC - Designation *
                      </label>
                      <input
                        type="text"
                        value={poc.pocDesignation}
                        onChange={(e) =>
                          handlePocFieldChange(
                            poc.id!,
                            POC_DESIGNATION,
                            e.target.value
                          )
                        }
                        placeholder="Please enter"
                        className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 ${
                          validationErrors[`${poc.id}-${POC_DESIGNATION}`]
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                        disabled={!hasRole("SALES_OPS") && !hasRole("LABS_TEAM_HEAD") && !hasRole("SUPER_ADMIN")}
                      />
                      {validationErrors[`${poc.id}-${POC_DESIGNATION}`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors[`${poc.id}-${POC_DESIGNATION}`]}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        POC - Phone *
                      </label>
                      <input
                        type="text"
                        value={poc.pocPhone}
                        onChange={(e) =>
                          handlePocFieldChange(
                            poc.id!,
                            POC_PHONE,
                            e.target.value
                          )
                        }
                        placeholder="Please enter"
                        className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 ${
                          validationErrors[`${poc.id}-${POC_PHONE}`]
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                        disabled={!hasRole("SALES_OPS") && !hasRole("LABS_TEAM_HEAD") && !hasRole("SUPER_ADMIN")}
                      />
                      {validationErrors[`${poc.id}-${POC_PHONE}`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {validationErrors[`${poc.id}-${POC_PHONE}`]}
                        </p>
                      )}
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          POC - Email
                        </label>
                        <input
                          type="text"
                          value={poc.pocEmail}
                          onChange={(e) =>
                            handlePocFieldChange(
                              poc.id!,
                              POC_EMAIL,
                              e.target.value
                            )
                          }
                          placeholder="Please enter"
                          className={`w-full px-3 py-2 border rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 ${
                            validationErrors[`${poc.id}-${POC_EMAIL}`]
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-300 focus:ring-blue-500"
                          }`}
                          disabled={!hasRole("SALES_OPS") && !hasRole("LABS_TEAM_HEAD") && !hasRole("SUPER_ADMIN")}
                        />
                        {validationErrors[`${poc.id}-${POC_EMAIL}`] && (
                          <p className="text-red-500 text-xs mt-1">
                            {validationErrors[`${poc.id}-${POC_EMAIL}`]}
                          </p>
                        )}
                      </div>
                      {(hasRole("SALES_OPS") || hasRole("SUPER_ADMIN")) && (
                      <button
                        onClick={() => handleDeletePocLocal(poc.id!)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                        title="Delete POC"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                      )}  
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
              <div className="text-center py-8 text-gray-500">

                {(hasRole(LOGIN_ROLES.SALES_OPS) ||
                  hasRole(LOGIN_ROLES.SUPER_ADMIN)) &&
                  <>No POCs added yet. Click "Add More" to add your first POC.</>}

              </div>
          )}

          {/* Add More Button */}
          {(hasRole(LOGIN_ROLES.SALES_OPS) || hasRole(LOGIN_ROLES.SUPER_ADMIN)) && (
          <div className="flex justify-start">
            <Button
              onClick={handleAddNewRow}
              className="flex items-center gap-2 bg-primary text-white hover:bg-primary/90"
            >
              <PlusIcon className="w-4 h-4" />
              Add More
            </Button>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddEditSchoolPoc;
